import React from 'react';
import { motion } from 'motion/react';
import { Briefcase, Layers, Users, ShieldCheck } from 'lucide-react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

const Agencies = () => {
  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title="AI Social Media Tool for Agencies - Manage More Clients"
        description="Scale your agency with PlataPost's AI social media manager. Manage multiple brand profiles, collaborate with clients, and generate content faster."
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-40">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full glass-card border border-white/5 text-indigo-400 text-[10px] font-black uppercase tracking-widest leading-none">
              <Briefcase className="w-3.5 h-3.5" />
              <span>Agency Grade</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">AI for Social Media <br /> <span className="text-indigo-500">Agencies.</span></h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed font-medium">
              Scale your client roster without scaling your headcount. PlataPost's multi-brand dashboard and AI generation help your agency deliver 10x faster.
            </p>
            <Link to="/dashboard" className="inline-flex px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
               Scale My Agency
            </Link>
          </div>
          <div className="flex-1 w-full glass-card rounded-[4rem] p-12 relative overflow-hidden border border-white/5 shadow-2xl shadow-indigo-600/10 group">
             <div className="absolute inset-0 bg-indigo-600/10 blur-[100px] pointer-events-none" />
             <div className="grid grid-cols-2 gap-6 relative z-10">
                <div className="h-32 bg-white/5 rounded-2xl border border-white/10 animate-pulse transition-transform duration-700 group-hover:-translate-y-2" />
                <div className="h-32 bg-white/5 rounded-2xl border border-white/10 transition-transform duration-700 group-hover:translate-y-2 delayed-100" />
                <div className="h-32 bg-white/5 rounded-2xl border border-white/10 transition-transform duration-700 group-hover:-translate-y-2 delayed-200" />
                <div className="h-32 bg-white/5 rounded-2xl border border-white/10 animate-pulse transition-transform duration-700 group-hover:translate-y-2 delayed-300" />
             </div>
          </div>
        </div>

        <section className="mb-40">
          <div className="text-center mb-24 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">Built for Scale</h2>
            <p className="text-gray-400 font-medium max-w-xl mx-auto">Enterprise-ready management for high-growth agencies.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
             {[
               { icon: Layers, title: "Multi-Brand Hub", desc: "Manage 10+ clients from a single login with isolated brand voices." },
               { icon: Users, title: "Client Collaboration", desc: "Share plans with clients for review and approval without messy emails." },
               { icon: ShieldCheck, title: "White-Label Ready", desc: "Professional workflows that look like they came straight from your team." }
             ].map((feature, i) => (
                <div key={i} className="p-12 glass-card rounded-[3rem] border border-white/5 hover:bg-white/5 transition-all group">
                   <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-10 text-indigo-400 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl shadow-indigo-600/5">
                      <feature.icon className="w-10 h-10" />
                   </div>
                   <h3 className="text-3xl font-black text-white mb-6 tracking-tight">{feature.title}</h3>
                   <p className="text-gray-400 leading-relaxed font-medium">{feature.desc}</p>
                </div>
             ))}
          </div>
        </section>

        <div className="text-center glass-card rounded-[4rem] p-24 border border-white/5 relative overflow-hidden shadow-2xl shadow-indigo-600/5">
           <div className="absolute inset-0 bg-indigo-600/10 blur-[150px] pointer-events-none" />
           <h2 className="text-4xl lg:text-6xl font-black text-white mb-10 tracking-tighter">Ready to manage more <span className="text-indigo-400">clients?</span></h2>
           <Link to="/dashboard" className="relative z-10 inline-flex px-12 py-6 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
              Get an Agency Demo
           </Link>
        </div>
      </div>
    </div>
  );
};

export default Agencies;
